import { DownloadIcon } from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import Tabs from "components/lib/Shared/Tabs";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _merge from "lodash/merge";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useToast } from "hooks/useToast";
import { getSignatureModuleData } from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { handleFilesUpload } from "utils/filesUtils";
import { uploadTemplateFileAsync } from "state/slices/templateFile";
import { changeDocumentSignatureUpload } from "state/slices/initiateTransactionChange";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getTransactionsAsync } from "state/slices/transaction";
import { Constants } from "config/constants";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { addComment, editComment, getComments } from "state/slices/comment";

const initialTabs = [
  {
    id: "signatureDocuments",
    label: "Signature Documents",
  },
];

const DocumentSignature = ({ triggerAlert = null, row, visibleColumns }) => {
  const specificItem = row.original || row;
  const { toast } = useToast();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(initialTabs[0]);
  const [proceedToReviewFlow, setProceedToReviewFlow] = useState("default");
  const [isDocsDownloading, setIsDocsDownloading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const formName =
    specificItem.status ===
    Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE
      ? "changedDocumentsSignature"
      : specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE
      ? "directorDocumentsSignature"
      : specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
      ? "adminDocumentsSignature"
      : null;
  const signatureComments = comments.filter(
    (comment) => comment.formName === formName
  );
  const fileUploads =
    specificItem.status ===
    Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE
      ? row.original.uploadChangeDocuments.fileUploads
      : specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE
      ? specificItem.uploadDirectorApprovalDocs.fileUploads
      : specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
      ? specificItem.orgAdminReview.fileUploads
      : null;
  let documentToUpload = fileUploads;

  if (fileUploads) {
    documentToUpload = Object.values(fileUploads).reduce((acc, file) => {
      acc[file.fileType] = null;
      return acc;
    }, {});
  }
  const handleDownloadDocuments = async () => {
    try {
      setIsDocsDownloading(true);
      setProceedToReviewFlow("renderForms");
      const businessDocuments = [];
      const documentType =
        specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE
          ? "plateFormConsentToActForm"
          : specificItem.status ===
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE
          ? "plateFormDirectorDocument"
          : specificItem.status ===
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
          ? "BRSFormRegistrarDocument"
          : null;
      if (fileUploads) {
        let newArray = fileUploads;

        if (
          specificItem?.status ===
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
        ) {
          newArray = Object.values(fileUploads).reduce((acc, file) => {
            acc[file.fileType] = file.fileName;
            return acc;
          }, {});
        }
        getSignatureModuleData(documentType).map((businessDocument) => {
          const fileKey =
            newArray[businessDocument.name] ||
            company?.preSignedBusinessDocuments[businessDocument.name];
          if (fileKey) {
            businessDocuments.push({
              key: fileKey,
              name: `${businessDocument.label}.${fileKey.split(".").pop()}`,
            });
          }
        });
        await Promise.all(
          businessDocuments.map(async (businessDocument) => {
            return dispatch(
              downloadBusinessDocumentsAsync({
                companyId: row.original.companyId,
                businessDocument,
              })
            );
          })
        );
      }
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong, please try again"
      );
    } finally {
      setIsDocsDownloading(false);
    }
  };

  function createUploadFilePayloads(uploadedFiles, companyId, officialId) {
    return Object.keys(uploadedFiles).map((key) => {
      return {
        company: companyId,
        official: officialId,
        fileType: key,
        fileName: uploadedFiles[key],
        modeType: "not-signed-documents",
        subModeType: "self-resolution-type",
      };
    });
  }

  const submitChangeDocumentSignature = async () => {
    let fileType;
    if (
      specificItem.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE
    ) {
      fileType = "ConsentToAct";
    } else if (
      specificItem.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE
    ) {
      fileType = "DirectorResignationLetter";
    } else if (
      specificItem.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
    ) {
      fileType = "BoDResolutionsChanges";
    }

    setIsSubmitting(true);
    const constructFilesObject = (selectedFiles) => {
      let files = {};
      for (const key in selectedFiles.selectedFiles) {
        if (selectedFiles.selectedFiles.hasOwnProperty(key)) {
          if (key !== "documentToUpload") {
            files[key] = selectedFiles.selectedFiles[key];
          }
        }
      }

      return files;
    };
    const files = constructFilesObject(selectedFiles);
    try {
      const uploadedFiles = await handleFilesUpload(files);
      const uploadFilePayloads = createUploadFilePayloads(
        uploadedFiles,
        specificItem.companyId,
        specificItem.officialId
      );

      const uploadPromises = uploadFilePayloads.map((payload) => {
        return dispatch(uploadTemplateFileAsync({ data: payload }))
          .then((result) => result.payload.document._id)
          .catch((error) => {
            console.error(
              `Error uploading file with payload: ${payload}`,
              error
            );
            throw error;
          });
      });

      const uploadedFilesDataIds = await Promise.all(uploadPromises);
      let nextStatus;

      if (
        specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE
      ) {
        nextStatus =
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_APPROVAL;
      } else if (
        specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE
      ) {
        nextStatus =
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW;
      } else if (
        specificItem.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_FORM_SIGNATURE
      ) {
        nextStatus = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW;
      }
      const payloadData = {
        required: true,
        fileUploads: uploadedFilesDataIds,
        type: "documentSignature",
        status: nextStatus,
      };

      const uploadChangeDocumentSignature = await dispatch(
        changeDocumentSignatureUpload({
          companyId: specificItem.companyId,
          data: {
            payloadData,
            trxRef: specificItem.trxRef,
          },
        })
      );

      if (uploadChangeDocumentSignature.payload.status === true) {
        triggerAlert && triggerAlert();
        setIsSubmitting(false);
        dispatch(getTransactionsAsync({ limit: 10, skip: 0 }));
        history.push("/");
      }
    } catch (error) {
      console.error("Error in orgAdminReviewI:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: row.original.company._id,
            officialId: row.original._id,
            tabName: formName,
          },
        })
      );
    }
  };
  return (
    <>
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className={`px-9`}>
          <div className="mt-4 mb-2 rounded-md shadow-md m-auto py-4 px-4">
            <div className="relative">
              <h3 className="font-bold text-slate-800">Documents Upload</h3>
              <Tabs
                tabs={initialTabs}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
              />
              {selectedTab?.id === "signatureDocuments" && (
                <div class="flex space-x-4 mt-4 py-4 px-2">
                  <div class="flex-1 bg-gray-100 rounded-md shadow-md p-4">
                    <Formik
                      enableReinitialize
                      initialValues={{
                        fileUploads:
                          specificItem.status ===
                          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                            .CHANGES_SIGNATURE
                            ? {
                                ...specificItem.uploadChangeDocuments
                                  .fileUploads,
                              }
                            : specificItem.status ===
                              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                                .DIRECTOR_SIGNATURE
                            ? {
                                ...specificItem.uploadDirectorApprovalDocs
                                  .fileUploads,
                              }
                            : specificItem.status ===
                              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                                .BRS_FORM_SIGNATURE
                            ? { ...specificItem.orgAdminReview.fileUploads }
                            : {},
                      }}
                      onSubmit={() => null}
                    >
                      {() => {
                        const documentDataLabel =
                          specificItem.status ===
                          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                            .CHANGES_SIGNATURE
                            ? "plateFormConsentToActForm"
                            : specificItem.status ===
                              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                                .BRS_FORM_SIGNATURE
                            ? "BRSFormRegistrarDocument"
                            : "plateFormDirectorDocument";

                        return (
                          <div className="pl-4">
                            <Field
                              name="fileUploads"
                              component={FileUploadForm}
                              callFor={"OfficialSignature"}
                              documentData={getSignatureModuleData(
                                documentDataLabel
                              )}
                              onlyPreview
                              showDocumentLabel={true}
                              status={specificItem.status}
                              addReviewComment={true}
                              handleSaveComment={handleSaveComment}
                              formName={formName}
                              signatureComments={signatureComments}
                            />
                          </div>
                        );
                      }}
                    </Formik>
                  </div>
                  {proceedToReviewFlow === "renderForms" && (
                    <div class="flex-1 bg-white rounded-md shadow-md p-4">
                      <Formik
                        enableReinitialize
                        initialValues={{
                          fileUploads: { documentToUpload },
                        }}
                        onSubmit={() => null}
                      >
                        {() => {
                          const documentDataLabel =
                            specificItem.status ===
                            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                              .CHANGES_SIGNATURE
                              ? "plateFormConsentToActForm"
                              : specificItem.status ===
                                Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                                  .BRS_FORM_SIGNATURE
                              ? "BRSFormRegistrarDocument"
                              : "plateFormDirectorDocument";

                          return (
                            <div className="pl-4">
                              <Form>
                                <Field
                                  name="fileUploads"
                                  component={FileUploadForm}
                                  documentData={getSignatureModuleData(
                                    documentDataLabel
                                  )}
                                  disabled={false}
                                  selectedFiles={selectedFiles}
                                  setSelectedFiles={setSelectedFiles}
                                  isOrgAdminReview={true}
                                  showDocumentLabel
                                />
                              </Form>
                            </div>
                          );
                        }}
                      </Formik>
                    </div>
                  )}
                </div>
              )}
            </div>
            {proceedToReviewFlow !== "default" && (
              <div class="mt-4">
                <Button
                  onClick={() => handleDownloadDocuments()}
                  preIcon={DownloadIcon}
                >
                  {isDocsDownloading ? "Downloading..." : "Download Documents"}
                </Button>
              </div>
            )}
            <div className="flex justify-between">
              <div class="flex items-center space-x-2">
                <input
                  type="checkbox"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 m-4 rounded focus:ring-blue-500"
                />
                <span class="text-gray-900">
                  I confirmed all the forms have been correctly signed
                </span>
              </div>

              {proceedToReviewFlow !== "renderForms" && (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setProceedToReviewFlow("showDownloadButton")}
                  className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7 disabled:cursor-not-allowed"
                >
                  Proceed
                </Button>
              )}
              {proceedToReviewFlow === "renderForms" && (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={submitChangeDocumentSignature}
                  className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <SpinnerIcon />
                      Submitting...
                    </>
                  ) : (
                    "Submit for Review"
                  )}
                </Button>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
};

export default DocumentSignature;
